<template>
  <div class="occupancy-view">
    <!-- Header -->
    <div class="occupancy-header">
      <button class="occupancy-back-btn" @click="$router.go(-1)">
        <i class="mdi mdi-arrow-left" style="font-size: 20px;"></i>
      </button>
      <h1 class="occupancy-header-title">Location Monitoring</h1>

      <div class="occupancy-header-actions">
        <div class="occupancy-view-toggle">
          <button
            class="occupancy-toggle-btn"
            :class="{ active: currentView === 'table' }"
            @click="currentView = 'table'"
          >
            <i class="mdi mdi-view-headline" style="font-size: 18px;"></i>
            Table
          </button>
          <button
            class="occupancy-toggle-btn"
            :class="{ active: currentView === 'map' }"
            @click="currentView = 'map'"
          >
            <i class="mdi mdi-map" style="font-size: 18px;"></i>
            Map
          </button>
        </div>

        <div class="occupancy-office-count">
          <i class="mdi mdi-account-multiple-check" style="font-size: 18px; color: #4caf50;"></i>
          In Office: {{ inOfficeCount }}
        </div>

        <button class="occupancy-refresh-btn" @click="refreshAllUsersLocations" :disabled="isLoadingUsers">
          <i class="mdi mdi-refresh" :class="{ rotating: isLoadingUsers }" style="font-size: 20px;"></i>
        </button>
      </div>
    </div>

    <!-- Map View -->
    <div v-if="currentView === 'map'" class="occupancy-map-view-container">
      <OccupancyMapView
        ref="mapViewComponent"
        :locations="allUsersLocations"
        :is-loading="isLoadingUsers"
        :center-on-user="centerOnUser"
        @user-selected="viewUserHistory"
        @map-ready="onMapViewReady"
      />
    </div>

    <!-- Table View -->
    <div v-else>
      <!-- Controls -->
      <div class="occupancy-controls">
        <button class="occupancy-export-btn" @click="exportUserLocations" :disabled="allUsersLocations.length === 0">
          <i class="mdi mdi-file-document-outline" style="font-size: 18px;"></i>
          Export Data
        </button>
        <button class="occupancy-delete-btn" @click="deleteAllLocations" :disabled="allUsersLocations.length === 0">
          <i class="mdi mdi-delete" style="font-size: 18px;"></i>
          Delete Locations
        </button>
      </div>

      <!-- Loading -->
      <div v-if="isLoadingUsers" class="occupancy-loading-container">
        <div class="occupancy-loading-spinner"></div>
        <p class="occupancy-loading-text">Loading user locations...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="allUsersLocations.length === 0" class="occupancy-empty-state">
        <div class="occupancy-empty-icon">
          <i class="mdi mdi-file-question" style="font-size: 64px;"></i>
        </div>
        <h3 class="occupancy-empty-title">No User Locations</h3>
        <p class="occupancy-empty-description">Users need to start tracking to appear here</p>
      </div>

      <!-- Users Table -->
      <div v-else class="occupancy-table-container">
        <div class="occupancy-table-wrapper">
          <table class="occupancy-users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Location</th>
                <th>Accuracy</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in allUsersLocations" :key="user.device_id" class="occupancy-user-row">
                <td class="occupancy-user-cell">
                  <div class="occupancy-user-info">
                    <div class="occupancy-user-avatar">
                      <i class="mdi mdi-account" style="font-size: 20px;"></i>
                    </div>
                    <div class="occupancy-user-details">
                      <div class="occupancy-user-name">{{ user.device_id }}</div>
                    </div>
                  </div>
                </td>

                <td class="occupancy-location-cell">
                  <div class="occupancy-location-info">
                    <div class="occupancy-coordinates">{{ user.lat.toFixed(6) }}, {{ user.lon.toFixed(6) }}</div>
                    <div class="occupancy-altitude">
                      {{ Number.isFinite(user.altitude) ? `Alt: ${user.altitude.toFixed(1)}m` : 'No altitude' }}
                    </div>
                  </div>
                </td>

                <td class="occupancy-accuracy-cell">
                  <span class="occupancy-accuracy-badge" :class="getAccuracyClass(user.accuracy)">
                    {{ Number.isFinite(user.accuracy) ? `${user.accuracy.toFixed(1)}m` : 'N/A' }}
                  </span>
                </td>

                <td class="occupancy-status-cell">
                  <div class="occupancy-status-info">
                    <div class="occupancy-movement-status" :class="{ moving: user.is_moving }">
                      <i class="mdi" :class="user.is_moving ? 'mdi-run' : 'mdi-account'" style="font-size: 16px;"></i>
                      <span>{{ user.is_moving ? 'Moving' : 'Stationary' }}</span>
                    </div>
                    <div v-if="Number.isFinite(user.speed)" class="occupancy-speed">
                      {{ (user.speed * 3.6).toFixed(1) }} km/h
                    </div>
                  </div>
                </td>

                <td class="occupancy-time-cell">
                  <div class="occupancy-time-info">
                    <div class="occupancy-timestamp">{{ formatTime(user.ts) }}</div>
                    <div class="occupancy-time-ago" :class="getTimeAgoClass(user.ts)">{{ getTimeAgo(user.ts) }}</div>
                  </div>
                </td>

                <td class="occupancy-actions-cell">
                  <button class="occupancy-action-btn" @click="viewUserHistory(user.device_id)" title="View History">
                    <i class="mdi mdi-history" style="font-size: 16px;"></i>
                  </button>
                  <button class="occupancy-action-btn" @click="showUserOnMap(user)" title="Show on Map">
                    <i class="mdi mdi-map-marker" style="font-size: 16px;"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- User History Modal -->
    <div v-if="userHistoryDialog" class="occupancy-modal-overlay" @click="userHistoryDialog = false">
      <div class="occupancy-modal-content" @click.stop>
        <div class="occupancy-modal-header">
          <h3>Location History - {{ selectedUser }}</h3>
          <button class="occupancy-close-btn" @click="userHistoryDialog = false">
            <i class="mdi mdi-close" style="font-size: 20px;"></i>
          </button>
        </div>

        <div class="occupancy-modal-body">
          <div v-if="userHistoryLoading" class="occupancy-loading-container">
            <div class="occupancy-loading-spinner"></div>
            <p class="occupancy-loading-text">Loading history...</p>
          </div>

          <div v-else-if="userHistoryData.length === 0" class="occupancy-empty-state">
            <div class="occupancy-empty-icon">
              <i class="mdi mdi-map-marker-minus" style="font-size: 48px;"></i>
            </div>
            <h4 class="occupancy-empty-title">No History Found</h4>
          </div>

          <div v-else class="occupancy-history-list">
            <div v-for="item in userHistoryData" :key="item.id" class="occupancy-history-item">
              <div class="occupancy-history-icon" :class="{ moving: item.is_moving }">
                <i class="mdi" :class="item.is_moving ? 'mdi-run' : 'mdi-account'" style="font-size: 16px;"></i>
              </div>
              <div class="occupancy-history-content">
                <div class="occupancy-history-location">{{ item.lat.toFixed(6) }}, {{ item.lon.toFixed(6) }}</div>
                <div class="occupancy-history-details">
                  {{ formatDateTime(item.ts) }} •
                  Accuracy: {{ Number.isFinite(item.accuracy) ? `${item.accuracy.toFixed(1)}m` : 'N/A' }}
                  <span v-if="Number.isFinite(item.speed)"> • Speed: {{ (item.speed * 3.6).toFixed(1) }} km/h</span>
                </div>
              </div>
              <div class="occupancy-history-accuracy">
                <span class="occupancy-accuracy-badge" :class="getAccuracyClass(item.accuracy)">
                  {{ getAccuracyLevel(item.accuracy) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick ,defineExpose} from 'vue'
import OccupancyMapView from './OccupancyMapView.vue'
import AnalyticsController from '@/controllers/AnalyticsController'
import { showToast } from '@/services/utils'

// ───────────────────────────────────────────────────────────────────────────────
// State
// ───────────────────────────────────────────────────────────────────────────────
const allUsersLocations   = ref([])
const isLoadingUsers      = ref(false)
const autoRefreshTimer    = ref(null)

const userHistoryDialog   = ref(false)
const userHistoryData     = ref([])
const userHistoryLoading  = ref(false)
const selectedUser        = ref('')

const currentView         = ref('table') // 'table' | 'map'
const mapViewComponent    = ref(null)
const centerOnUser        = ref(null)

// Count devices seen within last 5 minutes
const inOfficeCount = computed(() => {
  const now = Date.now()
  return allUsersLocations.value.filter(u => {
    const ts = new Date(u?.ts || 0).getTime()
    return Number.isFinite(ts) && now - ts < 5 * 60 * 1000
  }).length
})

// ───────────────────────────────────────────────────────────────────────────────
function formatTime(ts) {
  if (!ts) return 'N/A'
  const d = new Date(ts)
  return Number.isFinite(d.getTime()) ? d.toLocaleTimeString() : 'N/A'
}
function formatDateTime(ts) {
  if (!ts) return 'N/A'
  const d = new Date(ts)
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : 'N/A'
}
function getTimeAgo(ts) {
  const t = new Date(ts).getTime()
  if (!Number.isFinite(t)) return 'Unknown'
  const mins = Math.floor((Date.now() - t) / 60000)
  if (mins < 1) return 'Just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}
function getTimeAgoClass(ts) {
  const t = new Date(ts).getTime()
  if (!Number.isFinite(t)) return 'text-grey'
  const mins = Math.floor((Date.now() - t) / 60000)
  if (mins < 5) return 'fresh'
  if (mins < 30) return 'recent'
  return 'old'
}
function getAccuracyClass(acc) {
  if (!Number.isFinite(acc)) return 'unknown'
  if (acc <= 5)  return 'excellent'
  if (acc <= 15) return 'good'
  return 'poor'
}
function getAccuracyLevel(acc) {
  if (!Number.isFinite(acc)) return 'Unknown'
  if (acc <= 5)  return 'High'
  if (acc <= 15) return 'Medium'
  return 'Low'
}

// ───────────────────────────────────────────────────────────────────────────────
// Data ops (AnalyticsController wired)
// ───────────────────────────────────────────────────────────────────────────────
async function fetchAllUsersLocations() {
  isLoadingUsers.value = true
  try {
    // Presence = latest per device (controller already maps API → UI)
    const rows = await AnalyticsController.getPresence({ sinceMinutes: 1440, limit: 3000 })
    allUsersLocations.value = rows
  } catch (e) {
    console.error('fetchAllUsersLocations error', e)
    allUsersLocations.value = []
    showToast({ text: 'Failed to load locations', type: 'error' })
  } finally {
    isLoadingUsers.value = false
  }
}

async function fetchUserHistory(deviceId, limit = 100) {
  userHistoryLoading.value = true
  try {
    const items = await AnalyticsController.getHistoryByDevice({
      deviceId,
      sinceMinutes: 1440,
      limit,
      order: 'desc'
    })
    userHistoryData.value = items
  } catch (e) {
    console.error('fetchUserHistory error', e)
    userHistoryData.value = []
    showToast({ text: 'Failed to load history', type: 'error' })
  } finally {
    userHistoryLoading.value = false
  }
}

async function refreshAllUsersLocations() {
  await fetchAllUsersLocations()
}

async function deleteAllLocations() {
  if (!confirm('Are you sure you want to delete all location data?')) return
  try {
    await AnalyticsController.deleteLocations() // no filters → delete everything
    showToast({ text: 'All location data deleted', type: 'success' })
    await fetchAllUsersLocations()
  } catch (e) {
    console.error('deleteAllLocations error', e)
    showToast({ text: 'Failed to delete location data', type: 'error' })
  }
}

function exportUserLocations() {
  if (!allUsersLocations.value.length) return
  const data = {
    exportTime: new Date().toISOString(),
    totalUsers: allUsersLocations.value.length,
    data: allUsersLocations.value.map(i => ({
      device_id: i.device_id,
      timestamp: i.ts,
      latitude: i.lat,
      longitude: i.lon,
      accuracy: i.accuracy,
      speed: i.speed,
      altitude: i.altitude,
      heading: i.heading,
      is_moving: i.is_moving,
      meta: i.meta
    }))
  }
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `all-users-locations-${new Date().toISOString().slice(0,10)}.json`
  document.body.appendChild(a); a.click(); document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ───────────────────────────────────────────────────────────────────────────────
// View helpers
// ───────────────────────────────────────────────────────────────────────────────
function setupAutoRefresh(intervalSeconds) {
  if (autoRefreshTimer.value) clearInterval(autoRefreshTimer.value)
  const n = Number(intervalSeconds)
  if (Number.isFinite(n) && n > 0) {
    autoRefreshTimer.value = setInterval(fetchAllUsersLocations, n * 1000)
  }
}
function viewUserHistory(deviceId) {
  selectedUser.value = deviceId
  userHistoryDialog.value = true
  fetchUserHistory(deviceId)
}
function showUserOnMap(user) {
  currentView.value = 'map'
  centerOnUser.value = { lat: user.lat, lon: user.lon, device_id: user.device_id }
  nextTick(() => {
    if (mapViewComponent.value) {
      mapViewComponent.value.centerOnLocation(user.lat, user.lon)
      mapViewComponent.value.highlightUser(user.device_id)
    }
  })
}
function onMapViewReady() {
  if (centerOnUser.value && mapViewComponent.value) {
    mapViewComponent.value.centerOnLocation(centerOnUser.value.lat, centerOnUser.value.lon)
    mapViewComponent.value.highlightUser(centerOnUser.value.device_id)
    centerOnUser.value = null
  }
}

// ───────────────────────────────────────────────────────────────────────────────
// Lifecycle
// ───────────────────────────────────────────────────────────────────────────────
onMounted(async () => {
  await fetchAllUsersLocations()
  setupAutoRefresh(30) // refresh every 30s
})
onBeforeUnmount(() => {
  if (autoRefreshTimer.value) clearInterval(autoRefreshTimer.value)
})

// expose for template
defineExpose({
  refreshAllUsersLocations
})
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.occupancy-view {
  min-height: var(--app-viewport-height, 100vh);
  background: linear-gradient(135deg, #f8fbff 0%, #e3f2fd 100%);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
/* Header */
.occupancy-header {
  background: white;
  color: #333;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
}
.occupancy-back-btn {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  color: #333;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.occupancy-back-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}
.occupancy-header-title {
  font-size: 1.5rem;
  font-weight: 600;
  flex: 1;
}
.occupancy-header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.occupancy-view-toggle {
  display: flex;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  padding: 0.25rem;
}
.occupancy-toggle-btn {
  background: transparent;
  border: none;
  color: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}
.occupancy-toggle-btn:hover {
  color: #333;
  background: rgba(0, 0, 0, 0.05);
}
.occupancy-toggle-btn.active {
  background: rgba(0, 0, 0, 0.1);
  color: #333;
}
.occupancy-refresh-btn {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  color: #333;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.occupancy-refresh-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}
.occupancy-refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.rotating {
  animation: rotate 1s linear infinite;
}
@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.occupancy-office-count {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  color: #2e7d32;
}
/* Map View Container */
.occupancy-map-view-container {
  height: calc(var(--app-viewport-height, 100vh) - 200px);
  min-height: 400px;
}
/* Controls */
.occupancy-controls {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.occupancy-export-btn {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(33, 150, 243, 0.2);
}
.occupancy-export-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(33, 150, 243, 0.3);
}
.occupancy-export-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.occupancy-delete-btn {
  background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
  border: none;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.2);
}
.occupancy-delete-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(244, 67, 54, 0.3);
}
.occupancy-delete-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
/* Loading & Empty States */
.occupancy-loading-container, .occupancy-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1.5rem;
  text-align: center;
}
.occupancy-loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(79, 195, 247, 0.2);
  border-top: 3px solid #4fc3f7;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.occupancy-loading-text {
  margin-top: 1rem;
  color: #64b5f6;
  font-weight: 500;
}
.occupancy-empty-icon {
  color: #bbdefb;
  margin-bottom: 1rem;
}
.occupancy-empty-title {
  color: #1565c0;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.occupancy-empty-description {
  color: #64b5f6;
  font-size: 0.875rem;
}
/* Table */
.occupancy-table-container {
  padding: 0 1.5rem 1.5rem;
}
.occupancy-table-wrapper {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(79, 195, 247, 0.1);
}
.occupancy-users-table {
  width: 100%;
  border-collapse: collapse;
}
.occupancy-users-table thead {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
}
.occupancy-users-table th {
  color: white;
  font-weight: 600;
  padding: 1rem;
  text-align: left;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
}
.occupancy-user-row {
  border-bottom: 1px solid rgba(79, 195, 247, 0.1);
  transition: all 0.2s ease;
}
.occupancy-user-row:hover {
  background: rgba(79, 195, 247, 0.03);
}
.occupancy-users-table td {
  padding: 1rem;
  vertical-align: top;
}
/* Table Cells */
.occupancy-user-cell .occupancy-user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.occupancy-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.occupancy-user-name {
  font-weight: 600;
  color: #1a237e;
  font-size: 0.875rem;
}
.occupancy-user-device {
  font-size: 0.75rem;
  color: #64b5f6;
  text-transform: capitalize;
}
.occupancy-location-info .occupancy-coordinates {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.8rem;
  color: #1a237e;
  font-weight: 500;
}
.occupancy-location-info .occupancy-altitude {
  font-size: 0.75rem;
  color: #64b5f6;
  margin-top: 0.25rem;
}
.occupancy-accuracy-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.occupancy-accuracy-badge.excellent {
  background: rgba(76, 175, 80, 0.1);
  color: #2e7d32;
  border: 1px solid rgba(76, 175, 80, 0.3);
}
.occupancy-accuracy-badge.good {
  background: rgba(255, 152, 0, 0.1);
  color: #f57c00;
  border: 1px solid rgba(255, 152, 0, 0.3);
}
.occupancy-accuracy-badge.poor {
  background: rgba(244, 67, 54, 0.1);
  color: #c62828;
  border: 1px solid rgba(244, 67, 54, 0.3);
}
.occupancy-accuracy-badge.unknown {
  background: rgba(158, 158, 158, 0.1);
  color: #616161;
  border: 1px solid rgba(158, 158, 158, 0.3);
}
.occupancy-status-info .occupancy-movement-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #616161;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}
.occupancy-status-info .occupancy-movement-status.moving {
  color: #ff9800;
}
.occupancy-status-info .occupancy-speed {
  font-size: 0.75rem;
  color: #64b5f6;
}
.occupancy-time-info .occupancy-timestamp {
  font-size: 0.875rem;
  color: #1a237e;
  font-weight: 500;
}
.occupancy-time-info .occupancy-time-ago {
  font-size: 0.75rem;
  margin-top: 0.25rem;
}
.occupancy-time-info .occupancy-time-ago.fresh {
  color: #4caf50;
}
.occupancy-time-info .occupancy-time-ago.recent {
  color: #ff9800;
}
.occupancy-time-info .occupancy-time-ago.old {
  color: #f44336;
}
.occupancy-actions-cell {
  white-space: nowrap;
}
.occupancy-action-btn {
  background: rgba(79, 195, 247, 0.1);
  border: none;
  color: #2196f3;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 0.5rem;
  transition: all 0.2s ease;
}
.occupancy-action-btn:hover {
  background: rgba(79, 195, 247, 0.2);
  transform: translateY(-1px);
}
/* Modal */
.occupancy-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}
.occupancy-modal-content {
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}
.occupancy-modal-header {
  background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
  color: white;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.occupancy-modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
}
.occupancy-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}
.occupancy-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}
.occupancy-modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 1.5rem;
}
.occupancy-history-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.occupancy-history-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: rgba(79, 195, 247, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(79, 195, 247, 0.1);
}
.occupancy-history-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #9e9e9e;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
.occupancy-history-icon.moving {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}
.occupancy-history-content {
  flex: 1;
}
.occupancy-history-location {
  font-family: 'SF Mono', Monaco, monospace;
  font-size: 0.875rem;
  color: #1a237e;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.occupancy-history-details {
  font-size: 0.75rem;
  color: #64b5f6;
}
.occupancy-history-accuracy {
  flex-shrink: 0;
}
/* Responsive Design */
@media (max-width: 1200px) {
  .occupancy-users-table {
    font-size: 0.875rem;
  }
 
  .occupancy-users-table th,
  .occupancy-users-table td {
    padding: 0.75rem;
  }
}
@media (max-width: 960px) {
  .occupancy-header {
    padding: 1rem;
  }
 
  .occupancy-header-title {
    font-size: 1.25rem;
  }
 
  .occupancy-header-actions {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
 
  .occupancy-controls {
    padding: 1rem;
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }
 
  .occupancy-table-container {
    padding: 0 1rem 1rem;
  }
 
  .occupancy-map-view-container {
    height: calc(var(--app-viewport-height, 100vh) - 250px);
  }
}
@media (max-width: 768px) {
  .occupancy-header {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
 
  .occupancy-header-actions {
    width: 100%;
    justify-content: space-between;
  }
 
  .occupancy-view-toggle {
    flex: 1;
  }
 
  .occupancy-toggle-btn {
    flex: 1;
    justify-content: center;
  }
 
  .occupancy-table-wrapper {
    overflow-x: auto;
  }
 
  .occupancy-users-table {
    min-width: 600px;
  }
 
  .occupancy-users-table th,
  .occupancy-users-table td {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
 
  .occupancy-user-avatar {
    width: 32px;
    height: 32px;
  }
 
  .occupancy-modal-content {
    margin: 0.5rem;
    max-height: 90vh;
  }
 
  .occupancy-modal-header {
    padding: 1rem;
  }
 
  .occupancy-modal-body {
    padding: 1rem;
  }
 
  .occupancy-map-view-container {
    height: calc(var(--app-viewport-height, 100vh) - 300px);
  }
}
</style>
